import React, { useState, useEffect } from 'react';
import { getToken } from '../../utils/tokenUtils';
import { Download, Eye, X, Search, Calendar, Filter, FileText, DollarSign, TrendingDown, TrendingUp } from 'lucide-react';
import StatusBadge from '../../components/UI/StatusBadge';

const MyPayslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: ''
  });
  const token = getToken();

  useEffect(() => {
    fetchPayslips();
  }, [filters]);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ per_page: 50 });
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);

      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/my-payrolls?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      setPayslips(data.data.data || []);
    } catch (error) {
      console.error('Error fetching payslips:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPayslip = async (recordId, period) => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/payslip/${recordId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `payslip_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading payslip:', error);
      alert('Error downloading payslip');
    }
  };

  const viewPayslipDetails = (payslip) => {
    setSelectedPayslip(payslip);
    setShowDetailsModal(true);
  };

  const downloadAllPayslips = async () => {
    if (payslips.length === 0) return;
    
    // Download each payslip - in real app, you might want to create a ZIP
    for (const payslip of payslips) {
      await downloadPayslip(payslip.id, `${months[payslip.month - 1]}_${payslip.year}`);
      // Add small delay to prevent overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);

  const getPayslipStatus = (payslip) => {
    if (payslip.status === 'paid') {
      return { color: 'bg-green-100 text-green-800', text: 'Paid' };
    } else if (payslip.status === 'approved') {
      return { color: 'bg-blue-100 text-blue-800', text: 'Approved' };
    } else {
      return { color: 'bg-yellow-100 text-yellow-800', text: 'Processing' };
    }
  };

  const getTrend = (current, previous) => {
    const currentVal = Number(current);
    const prevVal = Number(previous);

    if (isNaN(currentVal) || isNaN(prevVal) || prevVal === 0) {
      return null; // no trend available
    }

    const diff = currentVal - prevVal;
    const percentage = ((diff / prevVal) * 100).toFixed(2);

    return {
      isIncrease: diff > 0,
      isDecrease: diff < 0,
      percentage,
    };
  };


  return (
    <div className="p-3">
      <div>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Pay Slips</h1>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Download and view your monthly payslips
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={downloadAllPayslips}
              disabled={payslips.length === 0}
              className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-md flex items-center justify-center hover:bg-blue-700  disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              <Download className="h-4 w-4 mr-2" />
              Download All
            </button>
          </div>
        </div>

        {/* Filters */}
        {/* <div className="bg-white rounded-md shadow-md p-3 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.month}
                onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
              >
                <option value="">All Months</option>
                {months.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={() => setFilters({ year: new Date().getFullYear(), month: '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center"
              >
                <Filter className="h-4 w-4 mr-2" />
                Reset Filters
              </button>
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-md shadow-md p-3 mb-3">
          <div className="flex items-center justify-between gap-3">
            <div className='flex items-center gap-2'>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.year}
                  onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.month}
                  onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
                >
                  <option value="">All Months</option>
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className='mt-5'>
              <button
                onClick={() => setFilters({ year: new Date().getFullYear(), month: '' })}
                className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-100 flex items-center justify-center"
              >
                <Filter className="h-4 w-4 mr-2" />
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Payslips Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-8 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            ))
          ) : payslips.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Payslips Found</h3>
              <p className="text-gray-600">No payslips available for the selected period.</p>
            </div>
          ) : (
            payslips.map((payslip, index) => {
              const status = getPayslipStatus(payslip);
              const previousPayslip = payslips[index + 1];
              const trend = getTrend(payslip.net_salary, previousPayslip?.net_salary);
              
              return (
                <div key={payslip.id} className="bg-white rounded-md shadow-md p-3 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {months[payslip.month - 1]} {payslip.year}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Generated: {new Date(payslip.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span >
                      {/* {status.text} */}
                      <StatusBadge status={status?.text} />
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Gross Salary:</span>
                      <span className="text-sm font-medium text-gray-900">₹{payslip.gross_salary?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Deductions:</span>
                      <span className="text-sm font-medium text-red-600">₹{payslip.total_deductions?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-2">
                      <span className="text-sm font-semibold text-gray-900">Net Salary:</span>
                      <div className="flex items-center">
                        <span className="text-sm font-bold text-gray-900">
                          ₹
                          {!isNaN(Number(payslip.net_salary))
                            ? Number(payslip.net_salary).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : '0.00'}
                        </span>
                        {trend && (
                          <div className="ml-2 flex items-center">
                            {trend.isIncrease && <TrendingUp className="h-3 w-3 text-green-600 mr-1" />}
                            {trend.isDecrease && <TrendingDown className="h-3 w-3 text-red-600 mr-1" />}
                            <span
                              className={`text-xs ${
                                trend.isIncrease ? 'text-green-600' : trend.isDecrease ? 'text-red-600' : 'text-gray-500'
                              }`}
                            >
                              {trend.percentage}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => viewPayslipDetails(payslip)}
                      className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded-md shadow-md hover:bg-blue-100 flex items-center justify-center text-sm font-medium"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </button>
                    <button
                      onClick={() => downloadPayslip(payslip.id, `${months[payslip.month - 1]}_${payslip.year}`)}
                      className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded-md shadow-md hover:bg-blue-100 flex items-center justify-center text-sm font-medium"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Payslip Details Modal */}
        {showDetailsModal && selectedPayslip && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[1001]">
            <div className="relative top-10 mx-auto p-3 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Payslip Details 
                  </h3>
                  {/* <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button> */}

                  <button
                      type="button"
                      onClick={() => {
                      setShowDetailsModal(false);
                      }} 
                      className="group relative p-2 rounded-xl  hover:bg-red-50 dark:bg-gray-800/80 dark:hover:bg-red-900/20 transition-all duration-200 hover:scale-105 active:scale-95"
                      aria-label="Close modal">
                      <X 
                          size={20} 
                          className="text-gray-600 group-hover:text-red-500 dark:text-gray-400 dark:group-hover:text-red-400 transition-colors duration-200" 
                      />
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-red-500/0 to-red-600/0 group-hover:from-red-500/10 group-hover:to-red-600/10 transition-all duration-200"></div>
                  </button>
                </div>

                <div className="flex flex-col gap-3">
                  {/* Salary Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Salary Summary</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-gray-600">₹{selectedPayslip.gross_salary?.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">Gross</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-600">₹{selectedPayslip.total_deductions?.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">Deductions</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-600">₹{selectedPayslip.net_salary?.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">Net</p>
                      </div>
                    </div>
                  </div>

                  {/* Earnings Breakdown */}
                  {selectedPayslip.earnings_breakdown && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Earnings</h4>
                      <div className="space-y-2">
                        {selectedPayslip.earnings_breakdown.map((earning, index) => (
                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-700">{earning.name}</span>
                            <span className="text-sm font-medium text-green-600">₹{earning.amount?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Deductions Breakdown */}
                  {selectedPayslip.deductions_breakdown && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Deductions</h4>
                      <div className="space-y-2">
                        {selectedPayslip.deductions_breakdown.map((deduction, index) => (
                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-700">{deduction.name}</span>
                            <span className="text-sm font-medium text-red-600">₹{deduction.amount?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end items-center gap-2">
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="px-10 py-2 text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => downloadPayslip(selectedPayslip.id, `${months[selectedPayslip.month - 1]}_${selectedPayslip.year}`)}
                      className=" bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MyPayslips;